import React from 'react';

// 🦷 치식도 — 사분면 십자 표기 (환자 우측이 화면 왼쪽, 표준 관례)
//  8 7 6 5 4 3 2 1 │ 1 2 3 4 5 6 7 8   (상악: 1x │ 2x)
//  ────────────────┼────────────────
//  8 7 6 5 4 3 2 1 │ 1 2 3 4 5 6 7 8   (하악: 4x │ 3x)
// 선택 치아는 볼드·진하게, 나머지는 연한 회색
const R = [8, 7, 6, 5, 4, 3, 2, 1];
const L = [1, 2, 3, 4, 5, 6, 7, 8];

const cellStyle = (on) => ({
    display: 'inline-block', width: '1.25em', textAlign: 'center',
    fontWeight: on ? 800 : 400,
    color: on ? '#1E2230' : '#C4CAD6',
    fontSize: on ? '1.08em' : '0.92em',
    fontVariantNumeric: 'tabular-nums',
});

export function ToothChart({ teeth = [], style = {} }) {
    const set = new Set((teeth || []).map(t => parseInt(t, 10)).filter(n => !isNaN(n)));
    const cell = (fdi) => <span key={fdi} style={cellStyle(set.has(fdi))}>{fdi % 10}</span>;
    const half = { padding: '1px 8px', whiteSpace: 'nowrap' };
    return (
        <div style={{ display: 'inline-block', fontSize: 13.5, lineHeight: 1.7, ...style }}>
            <div style={{ display: 'flex' }}>
                <div style={{ ...half, borderRight: '1.5px solid #334155' }}>{R.map(d => cell(10 + d))}</div>
                <div style={half}>{L.map(d => cell(20 + d))}</div>
            </div>
            <div style={{ display: 'flex', borderTop: '1.5px solid #334155' }}>
                <div style={{ ...half, borderRight: '1.5px solid #334155' }}>{R.map(d => cell(40 + d))}</div>
                <div style={half}>{L.map(d => cell(30 + d))}</div>
            </div>
        </div>
    );
}

/** 인쇄 시트용 HTML 문자열 버전 */
export function toothChartHtml(teeth = []) {
    const set = new Set((teeth || []).map(t => parseInt(t, 10)).filter(n => !isNaN(n)));
    const cell = (fdi) => {
        const on = set.has(fdi);
        return `<span style="display:inline-block;width:1.25em;text-align:center;font-weight:${on ? 800 : 400};color:${on ? '#1e2230' : '#c4cad6'};font-size:${on ? '1.08em' : '0.92em'}">${fdi % 10}</span>`;
    };
    const half = 'padding:1px 8px;white-space:nowrap';
    return `<div style="display:inline-block;font-size:14px;line-height:1.7">
      <div style="display:flex">
        <div style="${half};border-right:1.5px solid #334155">${R.map(d => cell(10 + d)).join('')}</div>
        <div style="${half}">${L.map(d => cell(20 + d)).join('')}</div>
      </div>
      <div style="display:flex;border-top:1.5px solid #334155">
        <div style="${half};border-right:1.5px solid #334155">${R.map(d => cell(40 + d)).join('')}</div>
        <div style="${half}">${L.map(d => cell(30 + d)).join('')}</div>
      </div>
    </div>`;
}

/** teethDetails에서 고유 쉐이드 목록 */
export function shadesOf(details = []) {
    return [...new Set((details || []).map(d => d.shade).filter(Boolean))];
}

/** teethDetails의 고유 재료 목록 (복수 재료 표기용) */
export function materialsOf(details = [], fallback = '') {
    const list = [...new Set((details || []).map(d => d.material).filter(Boolean))];
    return list.length ? list.join(', ') : (fallback || '-');
}

/** 같은 보철·재료·쉐이드·비고 조합끼리 치아를 묶기 — 반복 표기 제거용
 *  반환: [{ teeth:[FDI…], prosthesis, material, shade, implant, isPontic, isBridge }] */
export function groupDetails(details = []) {
    const map = new Map();
    (details || []).forEach(d => {
        const key = [d.prosthesis || '', d.material || '', d.shade || '', d.implant || '', !!d.isPontic, !!d.isBridge].join('|');
        if (!map.has(key)) {
            map.set(key, {
                teeth: [],
                prosthesis: d.prosthesis || '', material: d.material || '', shade: d.shade || '',
                implant: d.implant || '', isPontic: !!d.isPontic, isBridge: !!d.isBridge,
            });
        }
        map.get(key).teeth.push(d.tooth);
    });
    const groups = [...map.values()];
    groups.forEach(g => g.teeth.sort((a, b) => (parseInt(a, 10) || 99) - (parseInt(b, 10) || 99)));
    // 크라운 등 본체 먼저, 폰틱 뒤로
    groups.sort((a, b) => (a.isPontic ? 1 : 0) - (b.isPontic ? 1 : 0));
    return groups;
}
